import type { AuthMeResponse, Locale, SessionUser } from '@warehouse/shared';

import { api, apiRequest } from '../lib/api-client';
import { isApiRequestError } from '../lib/api-error';

/** Thin typed wrappers over the api's auth surface (apps/api/src/auth/auth.controller.ts). */

export function fetchMe(signal?: AbortSignal): Promise<AuthMeResponse> {
  return apiRequest<AuthMeResponse>('/auth/me', { skipAuthRetry: false, ...(signal ? { signal } : {}) });
}

/** Hands the EMS JWT over ONCE; the response sets the httpOnly session cookies. */
export function exchangeEmsToken(token: string): Promise<AuthMeResponse> {
  return api.post<AuthMeResponse>('/auth/exchange', { token });
}

export function logout(): Promise<{ ok: true }> {
  return api.post<{ ok: true }>('/auth/logout');
}

export function updateProfileLanguage(language: Locale): Promise<SessionUser> {
  return api.patch<SessionUser>('/auth/profile', { language });
}

/**
 * `GET /api/auth/dev-login?email=…` (E2E escape hatch, DEV_AUTH_BYPASS).
 * It is a GET with a query param — not a POST — see auth.controller.ts.
 */
export function devLogin(email: string): Promise<AuthMeResponse> {
  return apiRequest<AuthMeResponse>('/auth/dev-login', { query: { email }, skipAuthRetry: true });
}

/**
 * Is the dev bypass live on this server?
 *
 * The api exposes no `/api/meta` and its 401 body is a bare `{code}`, so there
 * is nothing to read the flag off. We probe the endpoint itself WITHOUT the
 * `email` param and read the failure mode:
 *
 *   DEV_AUTH_BYPASS off → 404 `E_NOT_FOUND`   (the route refuses up front)
 *   DEV_AUTH_BYPASS on  → 400 `E_VALIDATION`  (zod rejects the missing email)
 *
 * No session is ever issued by the probe — `email` is required before any user
 * lookup happens. Anything unexpected (network, 5xx) is treated as "off": the
 * dev affordance must never be the reason a production login page breaks.
 */
export async function isDevLoginAvailable(signal?: AbortSignal): Promise<boolean> {
  try {
    await apiRequest('/auth/dev-login', { skipAuthRetry: true, ...(signal ? { signal } : {}) });
    // A 200 without an email should be impossible; treat it as available.
    return true;
  } catch (error) {
    if (isApiRequestError(error)) return error.status === 400;
    return false;
  }
}
